


























































































































import { Component, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import {
  minLenght,
  hasAtLeastNumber,
  hasAtLeastLetter
} from '@/utils/validationPassword';

import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import Typography from '@/components/atoms/Typography/Typography.vue';
import RulePassword from '@/componentsV2/molecules/RulePassword/RulePassword.vue';
import Service from '@/api/ForgotService';
import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';
import {
  getFirstServerMessage,
  translateAuthServerMessage
} from '@/utils/authServerMessage';
import flagKeys from '@/constants/flagKeys';
import HTTPStatus from '@/constants/httpStatus';

@Component({
  components: {
    FilledButton,
    Typography,
    ValidationProvider,
    ValidationObserver,
    Input,
    RulePassword
  }
})
export default class RecoveryPassword extends Vue {
  name = '';
  lastname = '';
  password = '';

  success = false;
  loading = false;

  enableFirstNameInput = false;
  enableLastNameInput = false;

  service = new Service();

  failedRulesPassword: string[] = [];

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  mounted() {
    if (!this.verifyType()) this.$router.push('/');

    if (this.$route.query.isSoftUser) this.verifySoftLogin();
  }

  verifySoftLogin() {
    const { name, lastname, hasFirstName, hasLastname } = this.$route
      .query as any;

    this.enableFirstNameInput = hasFirstName === 'false';
    this.enableLastNameInput = hasLastname === 'false';

    this.name = decodeURIComponent(name);
    this.lastname = decodeURIComponent(lastname);
  }

  async submit() {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    if (this.flagRulesPassword) {
      if (
        !this.isPasswordLengthValid ||
        !this.isPasswordNumberValid ||
        !this.isPasswordLetterValid
      ) {
        const failedRules = formRef.fields.password.failedRules;
        this.failedRulesPassword = Object.keys(failedRules);
        return null;
      }
    }

    if (!formIsValid) {
      setFocusOnFirstInput('input[aria-invalid="true"]');

      return;
    }

    this.loading = true;

    const token = this.$route.params.token;
    const typeUser = this.$route.params.typeUser;

    const data = {
      resetToken: token,
      newPassword: this.password,
      firstName: this.enableFirstNameInput ? this.name : undefined,
      lastName: this.enableLastNameInput ? this.lastname : undefined
    };

    let response;

    if (typeUser === 'customer') {
      response = await this.service.newCustomerPassword(data);
    } else {
      response = await this.service.newBusinessPassword(data);
    }

    this.success = response.status < 400;
    // The interceptor answers a 503 that has a body with a redirect to
    // /maintenance and a bare `{ status: 503 }`. A bodyless 503 is not
    // redirected and arrives as an AxiosError, which on axios 1.x also carries
    // `status: 503` — only the missing `response` tells the two apart.
    const sentToMaintenance =
      response.status === HTTPStatus.SERVICE_UNAVAILABLE && !response.response;
    if (!this.success && !sentToMaintenance) this.notifyServerError(response);

    this.loading = false;
  }

  /** The API toast is off for these endpoints (`api/index.ts`) so the message can be translated here. */
  notifyServerError(failedRequest: unknown) {
    const serverMessage = getFirstServerMessage(failedRequest);
    const message = serverMessage
      ? translateAuthServerMessage(serverMessage, this.$t.bind(this))
      : String(this.$t('common.authServerMessages.generic'));

    this.$notification(message, 'error');
  }

  clearFailedRulesPassword() {
    if (this.flagRulesPassword && this.$refs.observer) {
      this.failedRulesPassword = [];
    }
    return;
  }

  verifyType() {
    const typeUser = this.$route.params.typeUser;
    return typeUser === 'manager' || typeUser === 'customer';
  }

  handleGoToLogin() {
    const redirectLogin = this.$route.query.redirectLogin as string;

    if (redirectLogin) {
      return window.location.assign(redirectLogin);
    }

    this.$router.push({
      name: 'login',
      ...(this.isAxxessFlow && {
        query: this.$route.query
      })
    });
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get flagRulesPassword() {
    return this.flags.includes(flagKeys.enableRulesPassword);
  }

  get isPasswordLengthValid() {
    return minLenght(this.password);
  }

  get isPasswordNumberValid() {
    return hasAtLeastNumber(this.password);
  }

  get isPasswordLetterValid() {
    return hasAtLeastLetter(this.password);
  }

  get createNewPassFormCopies() {
    const variant = this.isAxxessFlow ? 'axxess' : 'default';

    return {
      title: String(this.$t('common.resetPassword.title')),
      description: String(
        this.$t(`common.resetPassword.description.${variant}`)
      )
    };
  }

  get successCopies() {
    const variant = this.isAxxessFlow ? 'axxess' : 'default';

    return {
      title: String(this.$t(`common.resetPassword.success.${variant}.title`)),
      description: String(
        this.$t(`common.resetPassword.success.${variant}.description`)
      ),
      actionLabel: String(
        this.$t(`common.resetPassword.success.${variant}.action`)
      )
    };
  }

  get rulesPassword() {
    if (this.isAxxessFlow) return 'required|axxess_password';
    if (this.flagRulesPassword) return 'required|min:8|has_letter|has_number';
    return 'required|min:5';
  }

  get passwordHelpText() {
    if (this.isAxxessFlow)
      return String(this.$t('common.resetPassword.helpText.axxess'));
    return String(this.$t('common.resetPassword.helpText.default'));
  }

  get isAxxessFlow() {
    return (
      this.$route.query.utm_source && this.$route.query.utm_source === 'axxess'
    );
  }

  get isCustomer() {
    return this.$route.params.typeUser === 'customer';
  }
}
